import React, { useState } from "react";
import Select from "react-select";
import MemberListModal from "./MemberListModal";

const TeamCreateModal = ({
  visible,
  title = "Team Create",
  onCancel,
  onSubmit,
  values,
  onChange,
  errors = {},
  dropdowns = {},
  disabled = false
}) => {
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [memberModalType, setMemberModalType] = useState(''); // 'partners' or 'teamName'

  if (!visible) return null;

  const handleFieldChange = (name, value) => {
    onChange({
      ...values,
      [name]: value,
    });
  };

  const handleMemberSelection = (selectedMemberIds) => {
    if (memberModalType === 'partner') {
      // Partners can have multiple selections
      handleFieldChange('partner', selectedMemberIds);
    } else if (memberModalType === 'head') {
      // Head can have multiple selections
      handleFieldChange('head', selectedMemberIds);
    }
  };

  const getSelectedHeadNames = () => {
    if (!values.head || values.head.length === 0) return '';
    
    // For Head - show multiple selections
    if (dropdowns.members && dropdowns.members.length > 0) {
      const names = values.head.map(headId => {
        const member = dropdowns.members.find(m => m.value === headId);
        return member ? member.label : '';
      }).filter(name => name);
      return `Head: ${names.join(', ')}`;
    }
    
    // Otherwise just show count
    return `Head: ${values.head.length} member(s) selected`;
  };

  const getSelectedPartnerNames = () => {
    if (!values.partner || values.partner.length === 0) return '';
    
    // For Partners - show multiple selections
    if (dropdowns.members && dropdowns.members.length > 0) {
      const names = values.partner.map(partnerId => {
        const member = dropdowns.members.find(m => m.value === partnerId);
        return member ? member.label : '';
      }).filter(name => name);
      return `Partners: ${names.join(', ')}`;
    }
    
    // Otherwise just show count
    return `Partners: ${values.partner.length} member(s) selected`;
  };

  // Function to check if chapter is required for selected contribution level
  const isChapterRequired = () => {
    if (!values.contributionLevel) return false;
    
    // Get the selected contribution level details
    const selectedContributionLevel = dropdowns.contributionLevels?.find(
      cl => cl.value === values.contributionLevel
    );
    
    // Define which contribution levels require chapter selection
    // You can modify this list based on your business logic
    const contributionLevelsRequiringChapter = [
      'Lead Contributor',
      'Senior Manager', 
      'Team Lead',
      'Assistant Manager'
      // Add more contribution level names that require chapter selection
    ];
    
    return selectedContributionLevel && 
           contributionLevelsRequiringChapter.includes(selectedContributionLevel.label);
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: errors[state.selectProps.name] ? '1px solid #dc3545' : '1px solid #ced4da',
      borderRadius: '4px',
      minHeight: '38px',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
      backgroundColor: state.isDisabled ? '#e9ecef' : provided.backgroundColor,
      opacity: state.isDisabled ? 0.6 : 1,
      cursor: state.isDisabled ? 'not-allowed' : 'default',
      '&:hover': {
        borderColor: state.isFocused ? '#80bdff' : '#adb5bd'
      }
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? '#6c757d' : '#6c757d',
      fontStyle: state.isDisabled ? 'italic' : 'normal'
    })
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" onClick={onCancel}></button>
          </div>

          <div className="modal-body">
            <div className="container-fluid">
              {/* Row 1: Contribution Level and Chapter */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">
                    Contribution Level<span className="text-danger">*</span>
                  </label>
                  <Select
                    styles={customSelectStyles}
                    name="contributionLevel"
                    options={dropdowns.contributionLevels || []}
                    placeholder="Select Contribution Level"
                    value={dropdowns.contributionLevels?.find(opt => opt.value === values.contributionLevel) || null}
                    onChange={(selected) => {
                      handleFieldChange('contributionLevel', selected?.value || '');
                    }}
                    isDisabled={disabled}
                  />
                  {errors.contributionLevel && (
                    <div className="text-danger small mt-1">{errors.contributionLevel}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Chapter
                  </label>
                  <Select
                    styles={customSelectStyles}
                    name="chapter"
                    options={dropdowns.chapters || []}
                    placeholder="Select Chapter"
                    value={dropdowns.chapters?.find(opt => opt.value === values.chapter) || null}
                    onChange={(selected) => handleFieldChange('chapter', selected?.value || '')}
                    isDisabled={disabled}
                  />
                  {errors.chapter && (
                    <div className="text-danger small mt-1">{errors.chapter}</div>
                  )}
                </div>
              </div>

              {/* Row 2: Core Team Master and Team */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">
                    Core Team Master<span className="text-danger">*</span>
                  </label>
                  <Select
                    styles={customSelectStyles}
                    name="coreTeamMaster"
                    options={dropdowns.coreTeamMasters || []}
                    placeholder="Select Core Team Master"
                    value={dropdowns.coreTeamMasters?.find(opt => opt.value === values.coreTeamMaster) || null}
                    onChange={(selected) => handleFieldChange('coreTeamMaster', selected?.value || '')}
                    isDisabled={disabled}
                  />
                  {errors.coreTeamMaster && (
                    <div className="text-danger small mt-1">{errors.coreTeamMaster}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Team<span className="text-danger">*</span>
                  </label>
                  <Select
                    styles={customSelectStyles}
                    name="team"
                    options={dropdowns.teams || []}
                    placeholder="Select Team"
                    value={dropdowns.teams?.find(opt => opt.value === values.team) || null}
                    onChange={(selected) => handleFieldChange('team', selected?.value || '')}
                    isDisabled={disabled}
                  />
                  {errors.team && (
                    <div className="text-danger small mt-1">{errors.team}</div>
                  )}
                </div>
              </div>

              {/* Row 3: Head and Partners */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label d-flex align-items-center">
                    Head<span className="text-danger">*</span>
                    <button 
                      type="button" 
                      className="btn btn-sm ms-2"
                      style={{ 
                        fontSize: '12px', 
                        padding: '2px 8px',
                        backgroundColor: '#C44C23',
                        color: '#fff',
                        border: 'none'
                      }}
                      onClick={() => {
                        setMemberModalType('head');
                        setShowMemberModal(true);
                      }}
                      disabled={disabled}
                    >
                      Add
                    </button>
                  </label>
                  
                  {/* Display selected head members */}
                  {values.head && values.head.length > 0 && (
                    <div 
                      className="form-control"
                      style={{ 
                        minHeight: '38px', 
                        backgroundColor: '#f8f9fa',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <span>{getSelectedHeadNames()}</span>
                    </div>
                  )}
                  {errors.head && (
                    <div className="text-danger small mt-1">{errors.head}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label d-flex align-items-center">
                    Partners
                    <span className="text-danger">*</span>
                    <button 
                      type="button" 
                      className="btn btn-sm ms-2 fs-6 lh-sm shadow-none border-0 btn-success"
                      onClick={() => {
                        setMemberModalType('partner');
                        setShowMemberModal(true);
                      }}
                      disabled={disabled}
                    >
                      Add
                    </button>
                  </label>
                  
                  {/* Display selected partners */}
                  {values.partner && values.partner.length > 0 && (
                    <div 
                      className="form-control"
                      style={{ 
                        minHeight: '38px', 
                        backgroundColor: '#f8f9fa',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <span>{getSelectedPartnerNames()}</span>
                    </div>
                  )}
                  {errors.partner && (
                    <div className="text-danger small mt-1">{errors.partner}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button 
              className="btn btn-secondary" 
              onClick={onCancel}
              disabled={disabled}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn fs-6 lh-sm shadow-none border-0 btn-success"
              onClick={onSubmit}
              disabled={disabled}
            >
              Submit
            </button>
          </div>
        </div>
      </div>

      {/* Member List Modal */}
      <MemberListModal
        visible={showMemberModal}
        onCancel={() => setShowMemberModal(false)}
        onSelectMembers={handleMemberSelection}
        selectedPartners={memberModalType === 'partner' ? (values.partner || []) : (values.head || [])}
      />
    </div>
  );
};

export default TeamCreateModal;
